import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import CampaignIcon from '@mui/icons-material/Campaign';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useGetPointsPurchaseOfferCampaigns } from '~/hooks/apis/loyalty/useGetPointsPurchaseOfferCampaigns';
import { useGetPointsPurchaseOffers } from '~/hooks/apis/loyalty/useGetPointsPurchaseOffers';
import {
  useCreatePointsPurchaseOffer,
  useCreatePointsPurchaseOfferCampaign,
  useUpdatePointsPurchaseOffer,
  useUpdatePointsPurchaseOfferCampaign,
} from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import type { PointsPurchaseOffer, PointsPurchaseOfferCampaign } from '~/services/LoyaltyService';

import ApiError from '~/types/ApiError';

interface OfferEditorState {
  offerId?: string;
  basePoints: string;
  publicPrice: string;
  bonusPercent: string;
  discountPercent: string;
  startDate: string;
  endDate: string;
}

function buildDefaultEditorState(): OfferEditorState {
  return {
    offerId: undefined,
    basePoints: '',
    publicPrice: '',
    bonusPercent: '0',
    discountPercent: '0',
    startDate: dayjs().format('YYYY-MM-DDTHH:mm'),
    endDate: dayjs().add(30, 'days').format('YYYY-MM-DDTHH:mm'),
  };
}

function normalizeCampaignType(type: string): 'bonus' | 'discount' | 'unknown' {
  const normalized = type.toLowerCase();
  if (normalized.includes('bonus')) return 'bonus';
  if (normalized.includes('discount')) return 'discount';
  return 'unknown';
}

function getActiveCampaignByType(
  campaigns: Array<PointsPurchaseOfferCampaign>,
  offerId: string,
  type: 'bonus' | 'discount',
): PointsPurchaseOfferCampaign | null {
  const candidateCampaigns = getApplicableCampaignsByType(campaigns, offerId, type);
  return candidateCampaigns[0] ?? null;
}

function getApplicableCampaignsByType(
  campaigns: Array<PointsPurchaseOfferCampaign>,
  offerId: string,
  type: 'bonus' | 'discount',
): Array<PointsPurchaseOfferCampaign> {
  const now = dayjs();
  return campaigns
    .filter((campaign) => {
      if (campaign.offerId !== offerId) return false;
      if ((campaign.status ?? '').toLowerCase() !== 'active') return false;
      if (campaign.startDate && now.isBefore(dayjs(campaign.startDate))) return false;
      if (campaign.endDate && now.isAfter(dayjs(campaign.endDate))) return false;
      return normalizeCampaignType(campaign.type) === type;
    })
    .sort((firstCampaign, secondCampaign) => {
      const firstTimestamp = dayjs(
        firstCampaign.startDate || firstCampaign.updatedAt || firstCampaign.createdAt || 0,
      ).valueOf();
      const secondTimestamp = dayjs(
        secondCampaign.startDate || secondCampaign.updatedAt || secondCampaign.createdAt || 0,
      ).valueOf();
      return secondTimestamp - firstTimestamp;
    });
}

function getPreferredCampaignType(
  campaigns: Array<PointsPurchaseOfferCampaign>,
  type: 'bonus' | 'discount',
): PointsPurchaseOfferCampaign['type'] {
  const matchingType = campaigns.find((campaign) => normalizeCampaignType(campaign.type) === type)?.type;
  if (matchingType) {
    return matchingType;
  }
  return type === 'bonus' ? 'BONUS_POINTS' : 'PRICE_DISCOUNT';
}

export default function LoyaltyPayForPointsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorState, setEditorState] = useState<OfferEditorState>(buildDefaultEditorState);
  const [isCreating, setIsCreating] = useState(false);
  const [offersById, setOffersById] = useState<Record<string, PointsPurchaseOffer>>({});

  const { data: offersResponse, isLoading: isOffersLoading } = useGetPointsPurchaseOffers('AUD');
  const { data: campaignsResponse, isLoading: isCampaignsLoading } = useGetPointsPurchaseOfferCampaigns();

  const createOfferMutation = useCreatePointsPurchaseOffer();
  const updateOfferMutation = useUpdatePointsPurchaseOffer();
  const createCampaignMutation = useCreatePointsPurchaseOfferCampaign();
  const updateCampaignMutation = useUpdatePointsPurchaseOfferCampaign();

  const apiOffers = useMemo<Array<PointsPurchaseOffer>>(() => offersResponse?.result ?? [], [offersResponse]);

  useEffect(() => {
    if (apiOffers.length === 0) return;
    setOffersById((previous) => {
      const next = { ...previous };
      for (const offer of apiOffers) {
        next[offer.id] = {
          ...(next[offer.id] ?? {}),
          ...offer,
        };
      }
      return next;
    });
  }, [apiOffers]);

  const offers = useMemo(() => {
    return Object.values(offersById).sort((firstOffer, secondOffer) => firstOffer.basePoints - secondOffer.basePoints);
  }, [offersById]);

  const campaigns = useMemo<Array<PointsPurchaseOfferCampaign>>(
    () => campaignsResponse?.result ?? [],
    [campaignsResponse],
  );

  const rows = useMemo(() => {
    return offers.map((offer) => {
      const bonusCampaign = getActiveCampaignByType(campaigns, offer.id, 'bonus');
      const discountCampaign = getActiveCampaignByType(campaigns, offer.id, 'discount');
      const hasAnyBonusCampaign = campaigns.some(
        (campaign) => campaign.offerId === offer.id && normalizeCampaignType(campaign.type) === 'bonus',
      );
      const hasAnyDiscountCampaign = campaigns.some(
        (campaign) => campaign.offerId === offer.id && normalizeCampaignType(campaign.type) === 'discount',
      );
      const bonusPercent = bonusCampaign?.value ?? (hasAnyBonusCampaign ? 0 : offer.bonusPointsPercent ?? 0);
      const discountPercent = discountCampaign?.value ?? (hasAnyDiscountCampaign ? 0 : offer.discountPercent ?? 0);
      const totalPoints = offer.totalPoints ?? Math.round(offer.basePoints + (offer.basePoints * bonusPercent) / 100);
      const effectivePrice =
        typeof offer.publicPrice === 'number'
          ? Math.max(0, offer.publicPrice - (offer.publicPrice * discountPercent) / 100)
          : null;
      // List endpoint doesn't expose offer-level createdAt/updatedAt — derive from campaigns when available.
      const createdAtCandidates = [bonusCampaign?.createdAt, discountCampaign?.createdAt]
        .filter((value): value is string => typeof value === 'string')
        .map((value) => dayjs(value).valueOf())
        .filter((value) => Number.isFinite(value));
      const effectiveCreatedAt =
        createdAtCandidates.length > 0 ? dayjs(Math.min(...createdAtCandidates)).toISOString() : undefined;
      const updatedAtCandidates = [bonusCampaign?.updatedAt, discountCampaign?.updatedAt]
        .filter((value): value is string => typeof value === 'string')
        .map((value) => dayjs(value).valueOf())
        .filter((value) => Number.isFinite(value));
      const effectiveUpdatedAt =
        updatedAtCandidates.length > 0 ? dayjs(Math.max(...updatedAtCandidates)).toISOString() : undefined;
      return {
        offer,
        bonusCampaign,
        discountCampaign,
        bonusPercent,
        discountPercent,
        totalPoints,
        effectivePrice,
        effectiveCreatedAt,
        effectiveUpdatedAt,
      };
    });
  }, [campaigns, offers]);

  const preferredBonusCampaignType = useMemo(() => getPreferredCampaignType(campaigns, 'bonus'), [campaigns]);
  const preferredDiscountCampaignType = useMemo(() => getPreferredCampaignType(campaigns, 'discount'), [campaigns]);

  const handleOpenCreate = () => {
    setIsCreating(true);
    setEditorState(buildDefaultEditorState());
    setIsEditorOpen(true);
  };

  const handleOpenEdit = (row: (typeof rows)[number]) => {
    setIsCreating(false);
    const seedCampaign = row.bonusCampaign ?? row.discountCampaign;
    setEditorState({
      offerId: row.offer.id,
      basePoints: String(row.offer.basePoints),
      publicPrice: String(row.offer.publicPrice ?? 0),
      bonusPercent: String(row.bonusPercent ?? 0),
      discountPercent: String(row.discountPercent ?? 0),
      startDate: seedCampaign?.startDate
        ? dayjs(seedCampaign.startDate).format('YYYY-MM-DDTHH:mm')
        : dayjs().format('YYYY-MM-DDTHH:mm'),
      endDate: seedCampaign?.endDate
        ? dayjs(seedCampaign.endDate).format('YYYY-MM-DDTHH:mm')
        : dayjs().add(30, 'days').format('YYYY-MM-DDTHH:mm'),
    });
    setIsEditorOpen(true);
  };

  const handleToggleOfferStatus = async (offer: PointsPurchaseOffer) => {
    const nextStatus = (offer.status ?? '').toLowerCase() === 'active' ? 'INACTIVE' : 'ACTIVE';
    try {
      await updateOfferMutation.mutateAsync({
        offerId: offer.id,
        params: {
          status: nextStatus,
        },
      });
      setOffersById((previous) => ({
        ...previous,
        [offer.id]: {
          ...(previous[offer.id] ?? offer),
          status: nextStatus,
          updatedAt: dayjs().toISOString(),
        },
      }));
      enqueueSnackbar(`Offer ${nextStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`Failed to update offer status: ${String(error)}`, { variant: 'error' });
    }
  };

  const upsertCampaign = async ({
    existingCampaign,
    offerId,
    type,
    value,
    startDate,
    endDate,
  }: {
    existingCampaign: PointsPurchaseOfferCampaign | null;
    offerId: string;
    type: 'bonus' | 'discount';
    value: number;
    startDate: string;
    endDate: string;
  }): Promise<string | null> => {
    if (existingCampaign) {
      await updateCampaignMutation.mutateAsync({
        campaignId: existingCampaign.id,
        params: {
          value,
          startDate: dayjs(startDate).toISOString(),
          endDate: endDate ? dayjs(endDate).toISOString() : null,
        },
      });
      return existingCampaign.id;
    }

    const createResponse = await createCampaignMutation.mutateAsync({
      offerId,
      type: type === 'bonus' ? preferredBonusCampaignType : preferredDiscountCampaignType,
      value,
      currency: 'AUD',
      startDate: dayjs(startDate).toISOString(),
      endDate: endDate ? dayjs(endDate).toISOString() : null,
    });
    return createResponse.result?.id ?? null;
  };

  const deactivateCampaignIfExists = async (existingCampaign: PointsPurchaseOfferCampaign | null) => {
    if (!existingCampaign) return;
    await updateCampaignMutation.mutateAsync({
      campaignId: existingCampaign.id,
      params: {
        endDate: dayjs().toISOString(),
      },
    });
  };

  const deactivateCampaigns = async (campaignsToDeactivate: Array<PointsPurchaseOfferCampaign>) => {
    await Promise.all(campaignsToDeactivate.map((campaign) => deactivateCampaignIfExists(campaign)));
  };

  const handleSave = async () => {
    const basePoints = Number(editorState.basePoints);
    const publicPrice = Number(editorState.publicPrice);
    const bonusPercent = Number(editorState.bonusPercent);
    const discountPercent = Number(editorState.discountPercent);

    if (!Number.isFinite(basePoints) || basePoints <= 0) {
      enqueueSnackbar('Base points must be greater than zero', { variant: 'error' });
      return;
    }
    if (!Number.isFinite(publicPrice) || publicPrice <= 0) {
      enqueueSnackbar('Price AUD must be greater than zero', { variant: 'error' });
      return;
    }

    try {
      let offerId = editorState.offerId;
      let currentBonusCampaign: PointsPurchaseOfferCampaign | null = null;
      let currentDiscountCampaign: PointsPurchaseOfferCampaign | null = null;

      if (isCreating) {
        const createResponse = await createOfferMutation.mutateAsync({
          basePoints,
          price: publicPrice,
          currency: 'AUD',
          validFrom: dayjs(editorState.startDate).toISOString(),
          title: `${basePoints.toLocaleString()} points`,
        });
        offerId = createResponse.result?.id;
      }

      if (!offerId) {
        enqueueSnackbar(
          'Offer updated, but campaign updates could not be applied automatically. Please reopen and save again.',
          { variant: 'warning' },
        );
        setIsEditorOpen(false);
        return;
      }

      const activeBonusCampaigns = getApplicableCampaignsByType(campaigns, offerId, 'bonus');
      const activeDiscountCampaigns = getApplicableCampaignsByType(campaigns, offerId, 'discount');
      currentBonusCampaign = activeBonusCampaigns[0] ?? null;
      currentDiscountCampaign = activeDiscountCampaigns[0] ?? null;

      if (bonusPercent > 0) {
        const activeBonusCampaignId = await upsertCampaign({
          existingCampaign: currentBonusCampaign,
          offerId,
          type: 'bonus',
          value: bonusPercent,
          startDate: editorState.startDate,
          endDate: editorState.endDate,
        });
        await deactivateCampaigns(activeBonusCampaigns.filter((campaign) => campaign.id !== activeBonusCampaignId));
      } else {
        await deactivateCampaigns(activeBonusCampaigns);
      }

      if (discountPercent > 0) {
        const activeDiscountCampaignId = await upsertCampaign({
          existingCampaign: currentDiscountCampaign,
          offerId,
          type: 'discount',
          value: discountPercent,
          startDate: editorState.startDate,
          endDate: editorState.endDate,
        });
        await deactivateCampaigns(
          activeDiscountCampaigns.filter((campaign) => campaign.id !== activeDiscountCampaignId),
        );
      } else {
        await deactivateCampaigns(activeDiscountCampaigns);
      }

      enqueueSnackbar(isCreating ? 'Offer created successfully' : 'Offer updated successfully', { variant: 'success' });
      setIsEditorOpen(false);
      setEditorState(buildDefaultEditorState());
    } catch (error) {
      if (error instanceof ApiError) {
        const details = error.errors ? ` (${JSON.stringify(error.errors)})` : '';
        enqueueSnackbar(`Failed to save offer: ${error.message}${details}`, { variant: 'error' });
        return;
      }
      enqueueSnackbar(`Failed to save offer: ${String(error)}`, { variant: 'error' });
    }
  };

  const isLoading = isOffersLoading || isCampaignsLoading;

  return (
    <Box>
      <Alert icon={<CampaignIcon />} severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Manage Pay for Points offers</AlertTitle>
        Create and manage points purchase offers shown to customers in the portal, including bonus and discount
        promotions with start and end dates.
      </Alert>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={2}>
        <Button variant="contained" onClick={handleOpenCreate}>
          Create offer
        </Button>
      </Stack>

      {isLoading ? (
        <Typography>Loading offers...</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Points Quantity</TableCell>
              <TableCell>Bonus Multiplier (%)</TableCell>
              <TableCell>Total Points</TableCell>
              <TableCell>Discount (%)</TableCell>
              <TableCell>Price AUD</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created at</TableCell>
              <TableCell>Updated at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.offer.id}
                sx={
                  (row.offer.status ?? '').toLowerCase() === 'active'
                    ? undefined
                    : {
                        opacity: 0.6,
                        bgcolor: 'action.hover',
                      }
                }
              >
                <TableCell>{row.offer.basePoints.toLocaleString()}</TableCell>
                <TableCell>{row.bonusPercent}</TableCell>
                <TableCell>{row.totalPoints.toLocaleString()}</TableCell>
                <TableCell>{row.discountPercent}</TableCell>
                <TableCell>
                  {typeof row.offer.publicPrice === 'number'
                    ? row.discountPercent > 0
                      ? `${row.effectivePrice?.toFixed(2)} (was ${row.offer.publicPrice.toFixed(2)})`
                      : row.offer.publicPrice.toFixed(2)
                    : '-'}
                </TableCell>
                <TableCell>{row.offer.status}</TableCell>
                <TableCell>
                  {row.effectiveCreatedAt ? dayjs(row.effectiveCreatedAt).format('DD/MM/YYYY HH:mm') : '-'}
                </TableCell>
                <TableCell>
                  {row.effectiveUpdatedAt ? dayjs(row.effectiveUpdatedAt).format('DD/MM/YYYY HH:mm') : '-'}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button size="small" variant="text" onClick={() => handleOpenEdit(row)}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      disabled={(row.offer.status ?? '').toLowerCase() !== 'active'}
                      onClick={() => handleToggleOfferStatus(row.offer)}
                    >
                      Deactivate
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      disabled={(row.offer.status ?? '').toLowerCase() === 'active'}
                      onClick={() => handleToggleOfferStatus(row.offer)}
                    >
                      Reactivate
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={isEditorOpen} onClose={() => setIsEditorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isCreating ? 'Create points offer' : 'Edit points offer'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Points Quantity"
              type="number"
              value={editorState.basePoints}
              onChange={(event) => setEditorState((prev) => ({ ...prev, basePoints: event.target.value }))}
              disabled={!isCreating}
            />
            <TextField
              label="Price AUD"
              type="number"
              value={editorState.publicPrice}
              onChange={(event) => setEditorState((prev) => ({ ...prev, publicPrice: event.target.value }))}
              disabled={!isCreating}
              helperText={isCreating ? '' : 'Price updates are not supported for existing offers'}
            />
            <TextField
              label="Bonus Multiplier (%)"
              type="number"
              value={editorState.bonusPercent}
              onChange={(event) => setEditorState((prev) => ({ ...prev, bonusPercent: event.target.value }))}
            />
            <TextField
              label="Discount (%)"
              type="number"
              value={editorState.discountPercent}
              onChange={(event) => setEditorState((prev) => ({ ...prev, discountPercent: event.target.value }))}
            />
            <TextField
              label="Effective Price AUD"
              value={(
                Number(editorState.publicPrice || 0) -
                (Number(editorState.publicPrice || 0) * Number(editorState.discountPercent || 0)) / 100
              ).toFixed(2)}
              disabled
            />
            <TextField
              label="Campaign Start"
              type="datetime-local"
              value={editorState.startDate}
              onChange={(event) => setEditorState((prev) => ({ ...prev, startDate: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Campaign End"
              type="datetime-local"
              value={editorState.endDate}
              onChange={(event) => setEditorState((prev) => ({ ...prev, endDate: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Total Points"
              value={(
                Number(editorState.basePoints || 0) +
                (Number(editorState.basePoints || 0) * Number(editorState.bonusPercent || 0)) / 100
              ).toFixed(0)}
              disabled
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
